import React, { useState, useEffect } from 'react';
import { X, Calendar, Camera, CheckCircle2 } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import {
  Select,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectItem,
} from "../ui/select";

import DatePicker from "components/generic/DatePicker";
import { GetUpcomingShoots, requestEquipment } from 'services/creatorService';
import { parseISO, isBefore } from "date-fns";

const EquipmentRequestFormModal = ({ open, onOpenChange, selectedItems, onSuccess }) => {
  const [purpose, setPurpose] = useState('project');
  const [loading, setLoading] = useState(false);
  const [crewMemberId, setCrewMemberId] = useState(null);
  const [step, setStep] = useState(0);
  const [projects, setProjects] = useState([]); // Store the upcoming projects
  const [formData, setFormData] = useState({
    projectId: '',
    otherPurposeReason: '',
    checkoutDate: null,
    returnDate: null
  });

  // const update = (key, value) => setFormData((prev) => ({ ...prev, [key]: value }));
  const update = (key, value) => {
  setFormData((prev) => {
    const newData = { ...prev, [key]: value };

    // LOGIC: If Checkout Date changes, check if current Return Date is still valid
    if (key === "checkoutDate" && newData.returnDate) {
      const checkDate = parseISO(value);
      const retDate = parseISO(newData.returnDate);

      // If the new checkout date is after the existing return date, reset return date
      if (isBefore(retDate, checkDate)) {
        newData.returnDate = null;
      }
    }
    return newData;
  });
};

  const handleSubmit = async (e) => {
  e.preventDefault();
  setLoading(true);

  const basePayload = {
    rental_purpose: purpose === 'project' ? 1 : 2,
    checkoutDate: formData.checkoutDate,
    expectedReturnDate: formData.returnDate,
    otherPurposeReason: purpose === 'other' ? formData.otherPurposeReason : undefined,
  };

  if (purpose === 'project') {
    basePayload.projectId = formData.projectId;
  }

  const requests = selectedItems.map(async (item) => {
    const payload = { ...basePayload, equipmentId: item.equipment_id };

    try {
      const response = await requestEquipment(payload);
      if (response.success) {
        console.log(`Request for equipment ${item.equipment_name} was successful`);
      } else {
        console.error(`Failed to request equipment ${item.equipment_name}`);
      }
    } catch (error) {
      console.error(`Error submitting request for equipment ${item.equipment_name}:`, error);
    }
  });

  await Promise.all(requests);

  setLoading(false);
  setStep(1);
  if (onSuccess) onSuccess();
};


  const handleClose = () => {
    onOpenChange(false);
    setTimeout(() => {
      setStep(0);
      setFormData({ projectId: '', otherPurposeReason: '', checkoutDate: null, returnDate: null });
    }, 300);
  };

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    const id = user?.crew_member_id;
    if (id) {
      setCrewMemberId(id);
    }
  }, []);

  useEffect(() => {
    if (!crewMemberId) return;
    fetchData();
  }, [crewMemberId]);

const fetchData = async () => {
  if (!crewMemberId) return;
  try {
    const [upcomingRes] = await Promise.all([
      GetUpcomingShoots({ crew_member_id: crewMemberId })
    ]);

    if (upcomingRes.success && Array.isArray(upcomingRes.data)) {
      const upcomingProjects = upcomingRes.data.map(p => ({
        // Convert to String here!
        project_id: String(p.project_id || p.id),
        project_name: p.project_name || 'Untitled Project'
      }));
      setProjects(upcomingProjects);
    }
  } catch (error) {
    console.error("Error fetching data:", error);
  }
};
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 lg:p-0">
      <div className="bg-[#FFF] dark:bg-card w-full max-w-[500px] rounded-2xl p-6 relative border border-[#E7E1DA] dark:border-border shadow-lg max-h-[90vh] overflow-y-auto">
        {/* Close Button */}
        {step !== 1 && (
          <button
            onClick={handleClose}
            className="absolute right-5 top-5 text-gray-500 dark:text-muted-foreground hover:text-black dark:hover:text-foreground"
          >
            <X size={20} />
          </button>
        )}

        {step === 0 ? (
          <>
            <h2 className="text-lg font-semibold mb-6 dark:text-foreground uppercase tracking-tight">
              Request Equipment
            </h2>

            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Selected Items Summary */}
              <div className="bg-[#F6F2ED] dark:bg-secondary/20 p-4 rounded-xl border border-[#ECD8B9] dark:border-border">
                <p className="text-[11px] font-bold uppercase text-[#A59479] mb-2 tracking-wider">
                  Selected Items ({selectedItems.length})
                </p>
                <div className="flex flex-wrap gap-2">
                  {selectedItems.map((item, idx) => (
                    <span
                      key={idx}
                      className="text-[11px] bg-white dark:bg-zinc-800 px-2 py-1 rounded-md border border-[#E7E1DA] dark:border-zinc-700 font-medium"
                    >
                      {item.equipment_name || item.equipment?.equipment_name}
                    </span>
                  ))}
                </div>
              </div>

              {/* Purpose Toggle */}
              <div className="space-y-3">
                <label className="block text-[11px] font-bold uppercase text-[#A59479] tracking-wider">
                  Purpose of Rental
                </label>
                <div className="flex gap-6">
                  <label className="flex items-center gap-2 cursor-pointer group">
                    <input
                      type="radio"
                      name="purpose"
                      value="project"
                      checked={purpose === "project"}
                      onChange={(e) => setPurpose(e.target.value)}
                      className="w-4 h-4 accent-[#A89C87]"
                    />
                    <span className="text-sm font-medium dark:text-zinc-300">
                      Project
                    </span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer group">
                    <input
                      type="radio"
                      name="purpose"
                      value="other"
                      checked={purpose === "other"}
                      onChange={(e) => setPurpose(e.target.value)}
                      className="w-4 h-4 accent-[#A89C87]"
                    />
                    <span className="text-sm font-medium dark:text-zinc-300">
                      Other
                    </span>
                  </label>
                </div>
              </div>

              {/* Project Selection Dropdown */}
              {purpose === "project" && (
                <div className="space-y-2">
                  <label className="text-sm font-medium dark:text-muted-foreground">
                    Select Project
                  </label>
                  <Select
                    value={formData.projectId}
                    onValueChange={(v) => update("projectId", v)}
                  >
                    <SelectTrigger className="dark:bg-background dark:border-border dark:text-foreground h-12">
                      <SelectValue placeholder="Choose a project..." />
                    </SelectTrigger>

                    <SelectContent className="dark:bg-card dark:border-border !h-auto max-h-60">
                      {projects.length > 0 ? (
                        projects.map((project) => (
                          <SelectItem
                            key={project.project_id}
                            value={project.project_id}
                          >
                            {project.project_name}
                          </SelectItem>
                        ))
                      ) : (
                        <SelectItem value="none" disabled>
                          No upcoming projects
                        </SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Other Purpose Reason and Dates */}
              {purpose === "other" && (
                <div className="space-y-4 animate-in fade-in slide-in-from-top-1 duration-300">
                  <div className="space-y-2">
                    <label className="text-sm font-medium dark:text-muted-foreground">
                      Reason / Purpose
                    </label>
                    <Textarea
                      required
                      placeholder="Enter the reason for this rental..."
                      value={formData.otherPurposeReason}
                      onChange={(e) =>
                        update("otherPurposeReason", e.target.value)
                      }
                      className="dark:bg-background dark:border-border dark:text-foreground min-h-[80px]"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <label className="text-sm font-medium dark:text-muted-foreground">
                        Checkout Date
                      </label>
                      <DatePicker
                        value={formData.checkoutDate}
                        onChange={(date) => update("checkoutDate", date)}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium dark:text-muted-foreground">
                        Return Date
                      </label>
                      <DatePicker
                        value={formData.returnDate}
                        onChange={(date) => update("returnDate", date)}
                        disabled={!formData.checkoutDate}
                        minDate={formData.checkoutDate}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Footer Actions */}
              <div className="flex justify-end gap-3 pt-6 mt-6 border-t border-[#E7E1DA] dark:border-border">
                <Button
                  variant="ghost"
                  type="button"
                  onClick={handleClose}
                  className="dark:text-foreground dark:hover:bg-secondary/20"
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  className="bg-button-coloured px-8"
                >
                  {loading ? "Processing..." : "Submit Request"}
                </Button>
              </div>
            </form>
          </>
        ) : (
          /* SUCCESS STEP */
          <div className="text-center py-10 animate-in zoom-in duration-300">
            <div className="w-16 h-16 bg-[#E7DDD4] dark:bg-secondary/30 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle2
                className="text-[#594736] dark:text-primary"
                size={32}
              />
            </div>
            <h3 className="text-xl font-semibold dark:text-foreground uppercase tracking-tight">
              Request Sent!
            </h3>
            <p className="text-sm text-gray-500 dark:text-muted-foreground mt-2 max-w-[250px] mx-auto">
              Your equipment request is pending approval.
            </p>
            <div className="mt-8">
              <Button
                onClick={handleClose}
                className="bg-button-coloured w-full max-w-[200px]"
              >
                Close
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EquipmentRequestFormModal;
